import React from 'react';
import { motion } from 'framer-motion';
import type { FilterType, SortType } from '../types';

interface FilterBarProps {
    activeFilter: FilterType;
    activeSort: SortType;
    searchValue: string;
    onFilterChange: (filter: FilterType) => void;
    onSortChange: (sort: SortType) => void;
    onSearchChange: (value: string) => void;
    stats: {
        total: number;
        active: number;
        critical: number;
        warning: number;
        resolved: number;
    };
}

const filters: { key: FilterType; label: string; count?: keyof FilterBarProps['stats'] }[] = [
    { key: 'all', label: 'Все', count: 'total' },
    { key: 'active', label: 'Активные', count: 'active' },
    { key: 'critical', label: 'Критические', count: 'critical' },
    { key: 'warning', label: 'Предупреждения', count: 'warning' },
    { key: 'resolved', label: 'Устранённые', count: 'resolved' }
];

const sorts: { key: SortType; label: string }[] = [
    { key: 'newest', label: 'Сначала новые' },
    { key: 'oldest', label: 'Сначала старые' },
    { key: 'severity', label: 'По важности' },
    { key: 'duration', label: 'По длительности' }
];

export const FilterBar: React.FC<FilterBarProps> = ({
    activeFilter,
    activeSort,
    searchValue,
    onFilterChange,
    onSortChange,
    onSearchChange,
    stats
}) => {
    return (
        <div className="px-[18px] pb-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                {filters.map(filter => {
                    const isActive = activeFilter === filter.key;
                    const count = filter.count ? stats[filter.count] : undefined;
                    
                    return (
                        <motion.button
                            key={filter.key}
                            onClick={() => onFilterChange(filter.key)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`
                                relative px-3 py-1.5 rounded-full text-xs font-medium
                                transition-all duration-200 border
                                ${isActive
                                    ? 'bg-[var(--brand)] border-[var(--brand)] text-white shadow-sm'
                                    : 'bg-[var(--bg-elevated)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-strong)] hover:text-[var(--text)]'
                                }
                            `}
                        >
                            {filter.label}
                            {count !== undefined && count > 0 && (
                                <span className={`
                                    ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                                    ${isActive ? 'bg-white/20' : 'bg-[var(--bg)] text-[var(--text-muted)]'}
                                `}>
                                    {count}
                                </span>
                            )}
                            {isActive && (
                                <motion.div
                                    layoutId="activeFilter"
                                    className="absolute inset-0 rounded-full border-2 border-[var(--brand)]"
                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                />
                            )}
                        </motion.button>
                    );
                })}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-[320px]">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="11" cy="11" r="8"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                </div>
                <input
                    type="text"
                    placeholder="Поиск по названию, описанию..."
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="
                        w-full pl-9 pr-3 py-2 rounded-lg
                        bg-[var(--bg-elevated)] border border-[var(--border)]
                        text-[13px] text-[var(--text)] placeholder:text-[var(--text-muted)]
                        focus:outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-dim)]
                        transition-all duration-200
                    "
                />
                    {searchValue && (
                        <button
                            onClick={() => onSearchChange('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text)]"
                        >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    )}
                </div>

                <select
                    value={activeSort}
                    onChange={(e) => onSortChange(e.target.value as SortType)}
                    className="
                        px-3 py-2 rounded-lg border border-[var(--border)]
                        bg-[var(--bg-elevated)] text-[var(--text)] text-[13px]
                        focus:outline-none focus:border-[var(--brand)]
                        cursor-pointer
                    "
                >
                    {sorts.map(sort => (
                        <option key={sort.key} value={sort.key}>{sort.label}</option>
                    ))}
                </select>
            </div>
        </div>
    );
};